import { useInitializeVaultKey } from '@/hooks/useVault';
import { useVaultKeyStore } from '@/store/vaultKeyStore';
import { initializeVault } from '@/utils/crypto/vaultCrypto';
import { authService } from '@/services/auth.service';
import { zodResolver } from '@hookform/resolvers/zod';
import { Check, Lock, X } from 'lucide-react';
import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { z } from 'zod';
import { toast } from 'sonner';

const initializeSchema = z.object({
  password: z.string().min(1, 'vault.validation.password.required'),
});

type InitializeFormData = z.infer<typeof initializeSchema>;

interface InitializeVaultModalProps {
  vaultId: string;
  isOpen: boolean;
  onClose: () => void;
}

export const InitializeVaultModal = ({
  vaultId,
  isOpen,
  onClose,
}: InitializeVaultModalProps) => {
  const { t } = useTranslation();
  const [isSuccess, setIsSuccess] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const initializeVaultMutation = useInitializeVaultKey(vaultId);
  const unlockVault = useVaultKeyStore((state) => state.unlockVault);

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
    reset,
  } = useForm<InitializeFormData>({
    resolver: zodResolver(initializeSchema),
  });

  const onSubmit = async (data: InitializeFormData) => {
    try {
      // 0. Verify password with backend first
      const verificationResponse = await authService.verifyPassword(data.password);

      if (!verificationResponse.data.isValid) {
        throw new Error(t('vault.initialize.invalidPassword', 'Mot de passe incorrect'));
      }

      // 1. Initialize vault encryption (generates master key, salt, AES-wrapped keys, and RSA keys)
      const { masterKey, initData } = await initializeVault(data.password);

      // 3. Send encrypted metadata to server
      await initializeVaultMutation.mutateAsync(initData);

      // 4. Store master key in memory (auto-lock after 30 minutes)
      unlockVault(vaultId, masterKey);

      // 5. Show success message
      setIsSuccess(true);

      // 6. Auto-close after 3 seconds
      setTimeout(() => {
        handleClose();
      }, 3000);
    } catch (error) {
      console.error('Failed to initialize vault:', error);
      if (error instanceof Error && error.message === 'Invalid login password') {
        // ideally set form error here
        toast.error(t('vault.initialize.invalidPassword', 'Mot de passe incorrect'));
      }
      // Error handled by hook (toast notification)
    }
  };

  const handleClose = () => {
    reset();
    setIsSuccess(false);
    setShowPassword(false);
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
      <div className="relative w-full max-w-lg rounded-2xl bg-white p-6 shadow-xl">
        {/* Close Button */}
        <button
          onClick={handleClose}
          className="absolute right-4 top-4 rounded-lg p-1 text-neutral-400 transition-colors hover:bg-neutral-100 hover:text-neutral-600"
        >
          <X className="h-5 w-5" />
        </button>

        {!isSuccess ? (
          <>
            {/* Header */}
            <div className="mb-6 text-center">
              <div className="mx-auto mb-4 flex h-16 w-16 items-center justify-center rounded-full bg-primary-100">
                <Lock className="h-8 w-8 text-primary-600" />
              </div>
              <h2 className="text-2xl font-bold text-neutral-900">
                {t('vault.initialize.title', 'Initialiser le Coffre-Fort')}
              </h2>
              <p className="mt-2 text-sm text-neutral-600">
                {t(
                  'vault.initialize.description',
                  'Confirmez votre mot de passe de connexion pour sécuriser votre coffre-fort',
                )}
              </p>
            </div>

            {/* Form */}
            <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
              {/* Password Field */}
              <div>
                <label
                  htmlFor="password"
                  className="mb-2 block text-sm font-medium text-neutral-700"
                >
                  {t('vault.initialize.loginPassword', 'Mot de passe de connexion')}
                  <span className="text-error-600">*</span>
                </label>
                <input
                  {...register('password')}
                  type={showPassword ? 'text' : 'password'}
                  id="password"
                  className="block w-full rounded-lg border border-neutral-300 px-3 py-2 shadow-sm transition-colors focus:border-primary-500 focus:ring-2 focus:ring-primary-100 focus:outline-none"
                  placeholder={t('vault.initialize.passwordPlaceholder', '••••••••••••')}
                />
                {errors.password && (
                  <p className="mt-1 text-sm text-error-600">
                    {t(errors.password.message as string)}
                  </p>
                )}
              </div>



              {/* Show Password Toggle */}
              <div className="flex items-center">
                <input
                  type="checkbox"
                  id="showPassword"
                  checked={showPassword}
                  onChange={(e) => setShowPassword(e.target.checked)}
                  className="h-4 w-4 rounded border-neutral-300 text-primary-600 focus:ring-primary-500"
                />
                <label htmlFor="showPassword" className="ml-2 text-sm text-neutral-700">
                  {t('vault.initialize.showPassword', 'Afficher le mot de passe')}
                </label>
              </div>

              {/* Security Info */}
              <div className="rounded-lg border border-success-200 bg-success-50 p-4">
                <div className="flex gap-2">
                  <Lock className="h-5 w-5 flex-shrink-0 text-success-600" />
                  <div className="text-sm text-success-800">
                    <p className="font-medium">
                      {t('vault.initialize.securityInfoTitle', 'Sécurité Zero-Knowledge')}
                    </p>
                    <p className="mt-1">
                      {t(
                        'vault.initialize.securityInfo',
                        "Nous utilisons votre mot de passe pour générer vos clés de chiffrement. Il n'est jamais stocké en clair sur nos serveurs.",
                      )}
                    </p>
                  </div>
                </div>
              </div>

              {/* Actions */}
              <div className="flex gap-3 pt-2">
                <button
                  type="button"
                  onClick={handleClose}
                  className="flex-1 rounded-lg border border-neutral-300 px-4 py-2 text-sm font-medium text-neutral-700 transition-colors hover:bg-neutral-50"
                >
                  {t('common.cancel', 'Annuler')}
                </button>
                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="flex flex-1 items-center justify-center gap-2 rounded-lg bg-primary-600 px-4 py-2 text-sm font-medium text-white transition-colors hover:bg-primary-700 disabled:opacity-50"
                >
                  <Lock className="h-4 w-4" />
                  {isSubmitting
                    ? t('common.initializing', 'Initialisation...')
                    : t('vault.initialize.submit', 'Initialiser')}
                </button>
              </div>
            </form>
          </>
        ) : (
          <>
            {/* Success Message */}
            <div className="text-center">
              <div className="mx-auto mb-4 flex h-16 w-16 items-center justify-center rounded-full bg-success-100">
                <Check className="h-8 w-8 text-success-600" />
              </div>
              <h2 className="text-2xl font-bold text-neutral-900">
                {t('vault.initialize.success', 'Coffre Initialisé !')}
              </h2>
              <p className="mt-2 text-sm text-neutral-600">
                {t(
                  'vault.initialize.successDescription',
                  'Votre coffre-fort est maintenant sécurisé avec un chiffrement AES-256-GCM.',
                )}
              </p>

              {/* Security Info */}
              <div className="mt-6 rounded-lg border border-success-200 bg-success-50 p-4">
                <div className="flex gap-2">
                  <Lock className="h-5 w-5 flex-shrink-0 text-success-600" />
                  <div className="text-left text-sm text-success-800">
                    <p className="font-medium">
                      {t('vault.initialize.encryptionEnabled', 'Chiffrement activé')}
                    </p>
                    <p className="mt-1">
                      {t(
                        'vault.initialize.securityDetails',
                        "Tous vos documents seront chiffrés avant d'être envoyés au serveur. Seul votre mot de passe permet d'y accéder.",
                      )}
                    </p>
                  </div>
                </div>
              </div>

              {/* Important Warning */}
              {/* <div className="mt-4 rounded-lg border border-warning-200 bg-warning-50 p-4">
                <div className="flex gap-2">
                  <AlertTriangle className="h-5 w-5 flex-shrink-0 text-warning-600" />
                  <p className="text-left text-sm text-warning-800">
                    {t(
                      'vault.initialize.passwordWarning',
                      'Important : Conservez votre mot de passe en lieu sûr. Si vous le perdez, vos données seront définitivement irrécupérables.',
                    )}
                  </p>
                </div>
              </div> */}

              <p className="mt-4 text-sm text-neutral-500">
                {t('vault.initialize.autoClose', 'Cette fenêtre se fermera automatiquement...')}
              </p>
            </div>
          </>
        )}
      </div>
    </div>
  );
};
